import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface SiteAd {
  id: string;
  ad_type: string;
  image_url: string;
  is_active: boolean;
  created_at: string;
}

interface SelectedAd {
  id: string;
  image_url: string;
}

interface SiteAdsResult {
  seller: SelectedAd | null;
  buyer: SelectedAd | null;
  market_value: SelectedAd | null;
  mortgage: SelectedAd | null;
  community_guide: SelectedAd | null;
}

// Randomly select one ad from an array
function selectRandomAd(ads: SiteAd[]): SelectedAd | null {
  if (ads.length === 0) return null;
  const randomIndex = Math.floor(Math.random() * ads.length);
  const selected = ads[randomIndex];
  return { id: selected.id, image_url: selected.image_url };
}

export function useSiteAds(siteId: string) {
  return useQuery({
    queryKey: ['site-ads', siteId],
    queryFn: async (): Promise<SiteAdsResult> => {
      const { data, error } = await supabase
        .from('site_ads')
        .select('id, ad_type, image_url')
        .eq('site_id', siteId)
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching site ads:', error);
        return { seller: null, buyer: null, market_value: null, mortgage: null, community_guide: null };
      }

      const ads = data as SiteAd[] || [];
      
      // Group ads by type
      const sellerAds = ads.filter(ad => ad.ad_type === 'seller');
      const buyerAds = ads.filter(ad => ad.ad_type === 'buyer');
      const marketValueAds = ads.filter(ad => ad.ad_type === 'market_value');
      const mortgageAds = ads.filter(ad => ad.ad_type === 'mortgage');
      const communityGuideAds = ads.filter(ad => ad.ad_type === 'community_guide');

      // Randomly select one from each category
      return {
        seller: selectRandomAd(sellerAds),
        buyer: selectRandomAd(buyerAds),
        market_value: selectRandomAd(marketValueAds),
        mortgage: selectRandomAd(mortgageAds),
        community_guide: selectRandomAd(communityGuideAds),
      };
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    enabled: !!siteId,
  });
}

// Function to track ad clicks via secure edge function with rate limiting
export async function trackSiteAdClick(adId: string) {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-ad-click`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ ad_id: adId, ad_type: 'site' }),
      }
    );
    if (!response.ok) {
      console.error('Error tracking ad click:', response.statusText);
    }
  } catch (err) {
    console.error('Error tracking ad click:', err);
  }
}
